// @ts-check
import { ref, onUnmounted, onMounted } from 'vue'
import { useActiveElement } from './useActiveElement'

const FOCUSABLE_ELEMENTS_SELECTOR =
  'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable], [tabindex]:not([tabindex="-1"])'

/**
 * 포커스 트랩을 관리하는 Composable
 * @param {{ initialFocus?: HTMLElement | null, isActive?: import('vue').Ref<boolean> }} [options]
 * @returns {{
 *   container: import('vue').Ref<HTMLElement | null>,
 *   activate: () => void,
 *   deactivate: () => void
 * }}
 */
export function useFocusTrap(options = {}) {
  const { initialFocus = null, isActive } = options
  /** @type {import('vue').Ref<HTMLElement | null>} */
  const container = ref(null)
  const { activeElement } = useActiveElement()
  /** @type {HTMLElement | null} */
  let elementToReturnFocusTo = null

  const getFocusableElements = () => {
    if (!container.value) return []
    return /** @type {HTMLElement[]} */ (Array.from(container.value.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR)))
  }

  /** @param {KeyboardEvent} e */
  const handleKeyDown = (e) => {
    if (e.key !== 'Tab' || (isActive && !isActive.value)) return

    const focusableElements = getFocusableElements()
    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault()
        lastElement.focus()
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault()
        firstElement.focus()
      }
    }
  }

  const activate = () => {
    if (!container.value) return
    if (activeElement.value instanceof HTMLElement) {
      elementToReturnFocusTo = activeElement.value
    }

    document.addEventListener('keydown', handleKeyDown)

    const focusTarget = initialFocus || getFocusableElements()[0]
    if (focusTarget) {
      focusTarget.focus()
    }
  }

  const deactivate = () => {
    document.removeEventListener('keydown', handleKeyDown)
    if (elementToReturnFocusTo) {
      requestAnimationFrame(() => {
        elementToReturnFocusTo?.focus()
      })
    }
  }

  onMounted(() => {
    if (isActive === undefined || isActive.value) {
      activate()
    }
  })

  onUnmounted(() => {
    deactivate()
  })

  return {
    container,
    activate,
    deactivate
  }
}













